/* #ifdef H5 */
import _uni from './uni.webview';
/* #endif */
import qs from 'qs';
import { getPlatform, toMerchantLogin, toLogin } from './methods';
import type { route } from '@config/types';
import type { Route } from '@config/routes';
import systemInfo from '@/stores/systemInfo';
import { routes, tabbar, nonNative, noLogin, needMerchLogin } from '@config/routes';

export const urlReg = /^(http(s)?:\/\/)[^/]+/;

// ===== 路由工具函数 =====

/** 分解url */
export const parseUrl = (url: string): route => {
	url.indexOf('://') != -1 && (url = url.replace(urlReg, ''));
	const arr = url.split('?');
	return {
		fullPath: url,
		path: arr[0],
		query: qs.parse(arr[1]),
	};
};

/** 根据路径查找路由信息 */
export const findRouteByPath = (path: string): Route | undefined => {
	return routes.find((route) => route.path === path);
};

/** 检查路径是否为项目内页面 */
const isLocal = (url: string): boolean => {
	const web_url = import.meta.env.VITE_WAP_BASE;
	return url.indexOf(web_url) != -1 || url.indexOf('http') == -1;
};

/** 获取当前路由 */
export const getRoute = (): route => {
	let route: route = {
		path: '',
		fullPath: '',
		query: {},
	};

	const pages = getCurrentPages();
	if (pages.length > 0) {
		const page: any = pages[pages.length - 1],
			fullPath = page.$page.fullPath;
		route = parseUrl(fullPath);
	}

	return route;
};

/** 返回上页 */
export const navigateBack = (num: number = -1) => {
	const delta = Math.abs(num),
		pages = getCurrentPages();

	if (pages.length > delta) {
		uni.navigateBack({ delta });
	} else {
		navigateTo('/pages/home');
	}
};

// ===== 路由分析和权限检查 =====

/** 获取路由信息 */
const getRouteInfo = (path: string) => {
	path = path.startsWith('/') ? path : `/${path}`;

	return {
		isTabbar: tabbar.includes(path),
		isNonNative: nonNative.includes(path),
		needsLogin: !noLogin.includes(path),
		needsMerchLogin: needMerchLogin.includes(path),
		routeData: routes.find((route) => route.path === path),
	};
};

/** 检查登录状态 */
const checkAuthStatus = (routeInfo: ReturnType<typeof getRouteInfo>, url: string): boolean => {
	const { token, token2 } = systemInfo();

	// 检查普通用户登录
	if (routeInfo.needsLogin && !token) {
		toLogin();
		return false;
	}

	// 检查商户登录
	if (routeInfo.needsMerchLogin && !token2) {
		toMerchantLogin(url);
		return false;
	}

	return true;
};

// ===== 平台特定的导航处理 =====

/** 执行基础导航 */
const executeNavigation = (url: string, type: string) => {
	if (type === 'replace') {
		uni.redirectTo({ url });
	} else {
		uni.navigateTo({ url });
	}
};

/** 处理 Tabbar 导航 */
const handleTabbarNavigation = (path: string, query: any, url: string) => {
	uni.setStorageSync(path, query);
	uni.switchTab({ url });
};

/** 处理非原生路由 */
const handleNonNativeRoute = (url: string, type: string) => {
	const platform = getPlatform();

	if (platform === 'h5') {
		executeNavigation(url, type);
	} else {
		uni.navigateTo({
			url: `/pages/webview?url=${encodeURIComponent(url)}`,
		});
	}
};

/** 处理 H5 平台的原生路由 */
const handleH5NativeRoute = (
	routeInfo: ReturnType<typeof getRouteInfo>,
	query: any,
	url: string,
	type: string,
) => {
	const { plat } = systemInfo();

	switch (plat) {
		case 'ios':
		case 'android':
			// App 内的 H5，通过 postMessage 与原生通信
			_uni.postMessage({
				data: {
					type: 1,
					params: { url },
				},
			});
			break;
		default:
			// 浏览器环境
			if (routeInfo.isTabbar) {
				handleTabbarNavigation(routeInfo.routeData?.path || '', query, url);
			} else {
				executeNavigation(url, type);
			}
			break;
	}
};

/** 处理原生平台的路由 */
const handleNativeRoute = (
	routeInfo: ReturnType<typeof getRouteInfo>,
	query: any,
	url: string,
	type: string,
) => {
	if (routeInfo.isTabbar) {
		handleTabbarNavigation(routeInfo.routeData?.path || '', query, url);
	} else {
		executeNavigation(url, type);
	}
};

// ===== 主导航函数 =====

const navigate = (param: string | route, type: string = 'push') => {
	// 1. 解析路由参数
	let routeData: route;
	if (typeof param === 'object') {
		const { path, query } = param;
		type = param.type || type;
		param.fullPath = path + qs.stringify(query, { addQueryPrefix: true });
		routeData = param;
	} else {
		routeData = parseUrl(param);
	}

	const { path, query } = routeData;
	let url = routeData.fullPath;

	// 基础检查
	if (!path || !url) return;
	if (!url.startsWith('/')) url = `/${url}`;

	// 2. 获取路由信息
	const routeInfo = getRouteInfo(path);

	// 3. 权限检查
	if (!checkAuthStatus(routeInfo, url)) {
		return;
	}

	// 4. 根据路由类型和平台执行导航
	const platform = getPlatform();

	if (routeInfo.isNonNative) {
		// 非原生路由：通过 webview 打开
		handleNonNativeRoute(url, type);
	} else if (platform === 'h5') {
		// H5 平台的原生路由
		handleH5NativeRoute(routeInfo, query, url, type);
	} else {
		// 原生平台的路由
		handleNativeRoute(routeInfo, query, url, type);
	}
};

// ===== 公共导航接口 =====

/** 跳转封装 - 主要导航入口 */
export const navigateTo = (param: string | route | number, type: string = 'push'): void => {
	// 参数验证
	if (!param) return;

	// 数字参数：返回上一页
	if (typeof param === 'number') {
		navigateBack(param);
		return;
	}

	// 字符串参数：检查是否为外部链接
	if (typeof param === 'string' && !isLocal(param)) {
		handleExternalLink(param);
		return;
	}

	// 内部路由导航
	navigate(param, type);
};

/** 处理外部链接 */
const handleExternalLink = (url: string): void => {
	if (getPlatform() === 'h5') {
		location.href = url;
	} else {
		uni.navigateTo({
			url: `/pages/webview?url=${encodeURIComponent(url)}`,
		});
	}
};

// ===== 辅助导航函数 =====

/** 延迟返回上一页 */
export const delayBack = (msg = '', delta = -1, delay = 800): void => {
	if (msg) {
		uni.showToast({ title: msg, icon: 'none' });
	}
	setTimeout(() => navigateBack(delta), delay);
};

/** 跳转到商品详情页 */
export const toGoodsDetail = (item: { waresId: string | number }): void => {
	navigateTo({
		path: '/pages/goods/detail',
		query: { id: item.waresId },
	});
};

// ===== 路由信息查询函数 =====

/** 获取路由的导航栏配置 */
export const getRouteNavConfig = (path: string) => {
	const routeData = findRouteByPath(path);
	return routeData
		? {
				title: routeData.title,
				custom: routeData.custom,
				textStyle: routeData.textStyle,
				bgColor: routeData.bgColor,
			}
		: null;
};

/** 检查路径是否需要登录 */
export const isLoginRequired = (path: string): boolean => {
	return !noLogin.includes(path);
};

/** 检查路径是否需要商户登录 */
export const isMerchantLoginRequired = (path: string): boolean => {
	return needMerchLogin.includes(path);
};

/** 检查路径是否为 Tabbar 页面 */
export const isTabbarPage = (path: string): boolean => {
	return tabbar.includes(path);
};
